// The Brand HTTP client (Flow 3; packages/shared/src/http/validate.ts VALIDATE_ROUTES
// brand* routes - LANDED per the tech-lead's canonical route list,
// docs/phase-0/slice5-ux-flows.md:250-265 "D2, now final"). Every response parses
// through the SAME shared Zod schema apps/api validates against (app-engineering §2
// "parse, don't validate") - never a re-derived client-side shape for a landed contract.
// Sub-actions the canonical list does not name (a scoped regenerate distinct from the
// first generate, a per-mockup tweak, the add-on purchase) are PROPOSED below, flagged
// at declaration - the ux spec names these as genuinely open (slice5-ux-flows.md:246,
// §10 open items 2-3), never silently invented against a landed contract. Mirrors
// src/api/certify.ts's own LANDED/PROPOSED split.
import { z } from "zod";
import { ApprovalGate, BrandDirection, MockupSet, NonEmptyString } from "@nightcrew/shared";
import type { AppId, Result } from "@nightcrew/shared";
import { apiPost } from "./client.js";
import type { ClientError } from "./errors.js";

// ============================================================================
// LANDED (http/validate.ts VALIDATE_ROUTES.brandDirections[Approve]/mockups[Approve])
// ============================================================================

export const BrandDirectionsResponse = z.object({ directions: z.array(BrandDirection) });
export type BrandDirectionsResponse = z.infer<typeof BrandDirectionsResponse>;

/** POST /v1/apps/:appId/brand/directions - generates directions (D32). Called again
 * with no args it regenerates the same way; see `regenerateBrandDirections` below for
 * the distinct-route PROPOSED alternative some callers may want for the round counter. */
export const generateBrandDirections = (
  appId: AppId,
): Promise<Result<BrandDirectionsResponse, ClientError>> =>
  apiPost(`/v1/apps/${appId}/brand/directions`, BrandDirectionsResponse, {});

/** POST /v1/apps/:appId/brand/directions/approve - the first default-deny gate (D32). */
export const approveBrandDirection = (
  appId: AppId,
  directionId: string,
): Promise<Result<ApprovalGate, ClientError>> =>
  apiPost(`/v1/apps/${appId}/brand/directions/approve`, ApprovalGate, { directionId });

/** POST /v1/apps/:appId/brand/mockups - generates mockups for the approved direction (D32). */
export const generateMockups = (
  appId: AppId,
  directionId: string,
): Promise<Result<MockupSet, ClientError>> =>
  apiPost(`/v1/apps/${appId}/brand/mockups`, MockupSet, { directionId });

/** POST /v1/apps/:appId/brand/mockups/approve - the second, load-bearing default-deny
 * gate: Build proceeds only on this approval (D32, slice-plan.md:77). */
export const approveMockups = (
  appId: AppId,
  directionId: string,
): Promise<Result<ApprovalGate, ClientError>> =>
  apiPost(`/v1/apps/${appId}/brand/mockups/approve`, ApprovalGate, { directionId });

// ============================================================================
// PROPOSED (ux spec §10 open item 2: whether directions and mockups share one D55
// allowance counter or two separate ones is an open product question for the
// tech-lead - not resolved here; every shape below reuses the landed domain schemas,
// only the route/envelope is illustrative)
// ============================================================================

/** PROPOSED - a scoped regenerate within the D55 allowance (ux spec §6.2), kept as its
 * own route so a caller can distinguish "first pass" from "another round" for the
 * visible round counter ("Round 2 of 2 included") without a client-side guess. */
export const regenerateBrandDirections = (
  appId: AppId,
): Promise<Result<BrandDirectionsResponse, ClientError>> =>
  apiPost(`/v1/apps/${appId}/brand/directions/regenerate`, BrandDirectionsResponse, {});

/** PROPOSED - the D55/D56 add-on purchase past the included allowance (ux spec §6.2).
 * NEVER carries a price in the request body - the founder taps a single fixed,
 * owner-configured add-on that the server prices; this client never sends or invents
 * a figure (CLAUDE.md §8). */
export const purchaseBrandDirectionsAddon = (
  appId: AppId,
): Promise<Result<BrandDirectionsResponse, ClientError>> =>
  apiPost(`/v1/apps/${appId}/brand/directions/addon`, BrandDirectionsResponse, {});

/**
 * PROPOSED same-origin media asset URL builder (mirrors certify.ts's own
 * `publicTrustPageUrl` ASSUMED-same-origin precedent, src/api/certify.ts:96-109). No
 * media-serving route is landed anywhere in the canonical contract for a private
 * `MediaRef` handle (model/validate.ts:194-199 "never a public URL, a semantic alias of
 * NonEmptyString") - flagged for the tech-lead, not treated as settled. Callers pair
 * this with an `onError` fallback (never a broken-image icon) since this slice is
 * fixture-first (ADR-0021 decision 6, `brand.higgsfield_real` default-OFF).
 */
export const mediaAssetUrl = (ref: string): string =>
  `${window.location.origin}/v1/media/${encodeURIComponent(ref)}`;

/** PROPOSED - a scoped tweak to one mockup screen (ux spec §6.3), never a full
 * regenerate. NAMED CONTRACT GAP (mirrors certify.ts's `getCertificateDetail` caveat):
 * `MockupSet.screens` carries private `MediaRef` handles with no separate per-screen
 * id (model/validate.ts:213-217) - the screen ref itself doubles as the route's id
 * here; a real backend would likely mint a stable id instead. Flagged for the
 * tech-lead alongside the route itself, not treated as settled. */
export const TweakMockupResponse = z.object({ screen: NonEmptyString });
export type TweakMockupResponse = z.infer<typeof TweakMockupResponse>;

export const tweakMockup = (
  appId: AppId,
  directionId: string,
  screenRef: string,
  instruction: string,
): Promise<Result<TweakMockupResponse, ClientError>> =>
  apiPost(
    `/v1/apps/${appId}/brand/mockups/${encodeURIComponent(screenRef)}/tweak`,
    TweakMockupResponse,
    { directionId, instruction },
  );
